// app.authAdmin — WHICH sign-in methods an app offers, as ONE clean concept,
// straight from the browser (the console's Sign-in panel). No server function:
// the `manage-users` capability grants the cognito-idp + one S3 action, so
// `{slug}:admin` / `:owner` configure their own app's login and everyone else
// gets AccessDenied from AWS itself.
//
// THE ABSTRACTION. A builder thinks in methods — {password, emailCode,
// google} — not in Cognito's two-layer reality. We map intent to the levers
// that actually control each, hiding the fact that they live in different
// places:
//
//   password   → the app CLIENT's ALLOW_USER_PASSWORD_AUTH flow. Freely
//                removable, and removing it genuinely blocks password
//                sign-in (auth.signIn → USER_PASSWORD_AUTH is refused). The
//                pool's SignInPolicy must still LIST password (AWS forbids a
//                pool without it), but no s3p surface reaches that residual —
//                so from the platform's model, off is off.
//   emailCode  → EMAIL_OTP in the pool's first factors + ALLOW_USER_AUTH on
//                the client.
//   google/…   → third-party providers on the pool + the client's offer list.
//
// And the intent is PERSISTED to public/__s3p/auth.json (served at
// /__s3p/auth.json) so each app's sign-in UI renders exactly the enabled
// methods — the toggle in the console actually changes the app's login page.
//
// UpdateUserPool/UpdateUserPoolClient RESET omitted fields, so both echo the
// current config field-by-field (same discipline as the provisioning-side
// email flip).

import {
  CognitoIdentityProviderClient,
  CreateIdentityProviderCommand,
  DeleteIdentityProviderCommand,
  DescribeIdentityProviderCommand,
  DescribeUserPoolClientCommand,
  DescribeUserPoolCommand,
  ListIdentityProvidersCommand,
  UpdateIdentityProviderCommand,
  UpdateUserPoolClientCommand,
  UpdateUserPoolCommand,
} from "https://cdn.jsdelivr.net/npm/@aws-sdk/client-cognito-identity-provider@3.1054.0/+esm";
import {
  GetObjectCommand,
  PutObjectCommand,
  S3Client,
} from "https://cdn.jsdelivr.net/npm/@aws-sdk/client-s3@3.1054.0/+esm";

import { paths } from "./paths.ts";
import {
  DEFAULT_SIGNUP_POLICY,
  type SignupPolicy,
  normalizeSignupPolicy,
} from "./signup-policy.ts";

export interface AuthAdminConfig {
  region: string;
  userPoolId: string;
  /** The SPA app client — where the password flow + provider offers live. */
  clientId: string;
  /** The project bucket — where the public methods config is written. */
  bucket: string;
  credentials: () => Promise<{
    accessKeyId: string;
    secretAccessKey: string;
    sessionToken?: string;
  }>;
}

export interface ProviderSummary {
  name: string;
  type: string;
  clientId?: string;
}

/** The public shape the app's sign-in UI reads from /__s3p/auth.json. */
export interface AuthMethods {
  password: boolean;
  emailCode: boolean;
  /** WEB_AUTHN is an enabled first factor (passkey sign-in). */
  passkey: boolean;
  /** "email" (default): username IS the email. "handle": email-less pool —
   *  free-form usernames, auto-confirmed sign-ups, admin-only recovery.
   *  IMMUTABLE (set at provision; UsernameAttributes can't change). */
  usernameMode: "email" | "handle";
  /** Provider names offered ("Google", …). */
  providers: string[];
  /** Self-signup allowed (policy "open") — a signup UI shows provider buttons
   *  only when true. Published so unauthenticated signup pages can read it. */
  signupOpen: boolean;
}

export interface AuthConfigView extends AuthMethods {
  /** COGNITO_DEFAULT | DEVELOPER — email codes only SEND in DEVELOPER (SES)
   *  mode; the panel renders the gate, not a dead toggle. */
  emailSendingAccount: string;
  /** Full provider detail (for the "wired as client …" line). */
  providerDetail: ProviderSummary[];
  /** The sign-up policy, read in the SAME pass that derived `signupOpen` — so
   *  a panel rendering both never GETs `.s3p/auth/policy.json` twice. */
  signupPolicy: SignupPolicy;
}

export interface AuthAdminModule {
  describe(): Promise<AuthConfigView>;
  /** Turn the classic password flow on/off (client USER_PASSWORD_AUTH). */
  setPassword(on: boolean): Promise<void>;
  /** Turn the email one-time-code method on/off (pool EMAIL_OTP factor). */
  setEmailCode(on: boolean): Promise<void>;
  /** Create-or-update a provider and offer it on the SPA client. */
  configureProvider(spec: {
    name: string;
    type: "Google" | "OIDC" | "SAML" | "SignInWithApple" | "Facebook";
    clientId?: string;
    /** Omit when EDITING an existing provider to keep the stored secret —
     *  Cognito preserves client_secret on update when it isn't supplied
     *  (the same behavior the AWS console relies on). Required on create. */
    clientSecret?: string;
    scopes?: string;
    /** Type-specific ProviderDetails beyond client_id/secret/scopes:
     *  OIDC → { oidc_issuer, attributes_request_method };
     *  Apple → { team_id, key_id, private_key }; etc. Merged in. */
    details?: Record<string, string>;
    /** Cognito attribute mapping (defaults to email/email_verified/sub). */
    attributeMapping?: Record<string, string>;
  }): Promise<void>;
  removeProvider(name: string): Promise<void>;
  /** The sign-up policy (who may create an account) the runtime's PreSignUp
   *  trigger enforces. */
  getPolicy(): Promise<SignupPolicy>;
  setPolicy(policy: SignupPolicy): Promise<void>;
}

const PASSWORD_FLOW = "ALLOW_USER_PASSWORD_AUTH";
const OTP_FLOW = "ALLOW_USER_AUTH";

export function createAuthAdmin(cfg: AuthAdminConfig): AuthAdminModule {
  const cog = new CognitoIdentityProviderClient({
    region: cfg.region,
    credentials: cfg.credentials,
  });
  const s3 = new S3Client({ region: cfg.region, credentials: cfg.credentials });

  async function pool() {
    const r = await cog.send(new DescribeUserPoolCommand({ UserPoolId: cfg.userPoolId }));
    if (!r.UserPool) throw new Error("DescribeUserPool returned no pool");
    return r.UserPool;
  }
  async function spaClient() {
    const r = await cog.send(
      new DescribeUserPoolClientCommand({ UserPoolId: cfg.userPoolId, ClientId: cfg.clientId }),
    );
    if (!r.UserPoolClient) throw new Error("DescribeUserPoolClient returned no client");
    return r.UserPoolClient;
  }

  /** Echo-update the client with a patched flow list and/or provider list. */
  async function patchClient(patch: {
    flows?: string[];
    providers?: string[];
  }): Promise<void> {
    const c = await spaClient();
    await cog.send(
      new UpdateUserPoolClientCommand({
        UserPoolId: cfg.userPoolId,
        ClientId: cfg.clientId,
        ClientName: c.ClientName,
        RefreshTokenValidity: c.RefreshTokenValidity,
        AccessTokenValidity: c.AccessTokenValidity,
        IdTokenValidity: c.IdTokenValidity,
        TokenValidityUnits: c.TokenValidityUnits,
        ExplicitAuthFlows: (patch.flows ?? c.ExplicitAuthFlows) as typeof c.ExplicitAuthFlows,
        SupportedIdentityProviders: patch.providers ?? c.SupportedIdentityProviders,
        CallbackURLs: c.CallbackURLs,
        LogoutURLs: c.LogoutURLs,
        AllowedOAuthFlows: c.AllowedOAuthFlows,
        AllowedOAuthScopes: c.AllowedOAuthScopes,
        AllowedOAuthFlowsUserPoolClient: c.AllowedOAuthFlowsUserPoolClient,
        ...(c.PreventUserExistenceErrors
          ? { PreventUserExistenceErrors: c.PreventUserExistenceErrors }
          : {}),
      }),
    );
  }

  /** The PUBLISHED password intent (source of truth for whether the app OFFERS
   *  password sign-in). SOFT passwordless: the client's USER_PASSWORD_AUTH flow
   *  stays enabled so signup can silently authenticate a generated password, but
   *  the app never shows a password field — this flag drives that, not the flow.
   *  Undefined on first run (nothing published yet). */
  /** The private sign-up policy. Missing key → the default (open): a fresh
   *  project has no object here until someone edits the policy, so the 404 is
   *  the normal state, not an error. ONE reader — `describe` hands the result
   *  to the panel so the same key isn't fetched twice per load. */
  async function readPolicy(): Promise<SignupPolicy> {
    try {
      const r = await s3.send(
        new GetObjectCommand({ Bucket: cfg.bucket, Key: paths().authPolicy() }),
      );
      const body = await r.Body?.transformToString();
      if (body) return normalizeSignupPolicy(JSON.parse(body));
    } catch {
      // no policy → open by default
    }
    return DEFAULT_SIGNUP_POLICY;
  }

  const isOpen = (p: SignupPolicy) => p.mode !== "invite";

  async function publishedPassword(): Promise<boolean | undefined> {
    try {
      const r = await s3.send(
        new GetObjectCommand({ Bucket: cfg.bucket, Key: paths().authMethods() }),
      );
      const body = await r.Body?.transformToString();
      if (body) return (JSON.parse(body) as { password?: boolean }).password;
    } catch {
      // not published yet
    }
    return undefined;
  }

  function methodsOf(
    p: Awaited<ReturnType<typeof pool>>,
    c: Awaited<ReturnType<typeof spaClient>>,
    providers: string[],
    passwordIntent: boolean,
    signupOpen: boolean,
  ): AuthMethods {
    const factors = p.Policies?.SignInPolicy?.AllowedFirstAuthFactors ?? [];
    const handleMode = !(p.UsernameAttributes ?? []).includes("email");
    return {
      // Handle-mode pools never SURFACE a password (the client keeps the flow
      // enabled for the silent post-signup sign-in, but passkeys are the UX).
      password: handleMode ? false : passwordIntent,
      emailCode: factors.includes("EMAIL_OTP"),
      passkey: factors.includes("WEB_AUTHN"),
      usernameMode: handleMode ? "handle" : "email",
      providers,
      signupOpen,
    };
  }

  /** Publish the public config the app sign-in UI reads. Best-effort: a
   *  failure here doesn't roll back the Cognito change (the panel re-reads and
   *  re-publishes), but it's the same STS, so it normally succeeds. */
  async function publish(methods: AuthMethods): Promise<void> {
    await s3.send(
      new PutObjectCommand({
        Bucket: cfg.bucket,
        Key: paths().authMethods(),
        Body: JSON.stringify(methods, null, 2),
        ContentType: "application/json",
        CacheControl: "no-cache",
      }),
    );
  }

  /** After any change: re-read the live config and republish the public file. */
  async function reconcile(passwordOverride?: boolean): Promise<AuthMethods> {
    const [p, c, provs] = await Promise.all([
      pool(),
      spaClient(),
      cog.send(new ListIdentityProvidersCommand({ UserPoolId: cfg.userPoolId, MaxResults: 25 })),
    ]);
    const providerNames = (provs.Providers ?? [])
      .map((x) => x.ProviderName)
      .filter((n): n is string => Boolean(n));
    const passwordIntent =
      passwordOverride ??
      (await publishedPassword()) ??
      (c.ExplicitAuthFlows ?? []).includes(PASSWORD_FLOW);
    const methods = methodsOf(p, c, providerNames, passwordIntent, isOpen(await readPolicy()));
    await publish(methods);
    return methods;
  }

  return {
    async describe() {
      const [p, c, provs] = await Promise.all([
        pool(),
        spaClient(),
        cog.send(new ListIdentityProvidersCommand({ UserPoolId: cfg.userPoolId, MaxResults: 25 })),
      ]);
      const detail: ProviderSummary[] = [];
      for (const pr of provs.Providers ?? []) {
        if (!pr.ProviderName) continue;
        let clientId: string | undefined;
        try {
          const d = await cog.send(
            new DescribeIdentityProviderCommand({
              UserPoolId: cfg.userPoolId,
              ProviderName: pr.ProviderName,
            }),
          );
          clientId = d.IdentityProvider?.ProviderDetails?.client_id;
        } catch {
          /* summary only */
        }
        detail.push({ name: pr.ProviderName, type: pr.ProviderType ?? "?", clientId });
      }
      const passwordIntent =
        (await publishedPassword()) ?? (c.ExplicitAuthFlows ?? []).includes(PASSWORD_FLOW);
      const policy = await readPolicy();
      const m = methodsOf(
        p,
        c,
        detail.map((d) => d.name),
        passwordIntent,
        isOpen(policy),
      );
      return {
        ...m,
        emailSendingAccount: p.EmailConfiguration?.EmailSendingAccount ?? "COGNITO_DEFAULT",
        providerDetail: detail,
        signupPolicy: policy,
      };
    },

    async setPassword(on) {
      const c = await spaClient();
      const flows = new Set(c.ExplicitAuthFlows ?? []);
      // Guard: don't leave an app with NO way in. Password off is only allowed
      // when email codes (or a provider) can carry sign-in.
      if (!on) {
        const p = await pool();
        const otp = (p.Policies?.SignInPolicy?.AllowedFirstAuthFactors ?? []).includes("EMAIL_OTP");
        const hasProvider = (c.SupportedIdentityProviders ?? []).some((x) => x !== "COGNITO");
        if (!otp && !hasProvider) {
          throw new Error(
            "can't turn password off while it's the only way in — enable email codes or a provider first",
          );
        }
      }
      // SOFT: never remove the flow. Keep USER_PASSWORD_AUTH so signup can
      // silently sign a new user in with their generated password; the app just
      // stops SHOWING a password field. `on` is published as intent only.
      if (!flows.has(PASSWORD_FLOW)) {
        flows.add(PASSWORD_FLOW);
        await patchClient({ flows: [...flows] });
      }
      await reconcile(on);
    },

    async setEmailCode(on) {
      const p = await pool();
      if (on && !(p.UsernameAttributes ?? []).includes("email")) {
        throw new Error(
          "email codes can't be enabled on this pool — its accounts have no email addresses (handle mode)",
        );
      }
      const factors = new Set(p.Policies?.SignInPolicy?.AllowedFirstAuthFactors ?? ["PASSWORD"]);
      // PASSWORD stays listed always (AWS requires it). We add/remove EMAIL_OTP.
      factors.add("PASSWORD");
      if (on) factors.add("EMAIL_OTP");
      else factors.delete("EMAIL_OTP");
      await cog.send(
        new UpdateUserPoolCommand({
          UserPoolId: cfg.userPoolId,
          Policies: {
            ...(p.Policies ?? {}),
            SignInPolicy: { AllowedFirstAuthFactors: [...factors] as ("PASSWORD" | "EMAIL_OTP" | "SMS_OTP" | "WEB_AUTHN")[] },
          },
          DeletionProtection: p.DeletionProtection,
          LambdaConfig: p.LambdaConfig,
          AutoVerifiedAttributes: p.AutoVerifiedAttributes,
          EmailConfiguration: p.EmailConfiguration,
          AdminCreateUserConfig: p.AdminCreateUserConfig
            ? {
                AllowAdminCreateUserOnly: p.AdminCreateUserConfig.AllowAdminCreateUserOnly,
                InviteMessageTemplate: p.AdminCreateUserConfig.InviteMessageTemplate,
              }
            : undefined,
          AccountRecoverySetting: p.AccountRecoverySetting,
          UserPoolTier: p.UserPoolTier,
        }),
      );
      // The choice-based flow must be enabled on the client for EMAIL_OTP.
      if (on) {
        const c = await spaClient();
        const flows = new Set(c.ExplicitAuthFlows ?? []);
        if (!flows.has(OTP_FLOW)) {
          flows.add(OTP_FLOW);
          await patchClient({ flows: [...flows] });
        }
      }
      await reconcile();
    },

    async configureProvider(spec) {
      const details: Record<string, string> = {
        ...(spec.clientId ? { client_id: spec.clientId } : {}),
        ...(spec.scopes ? { authorize_scopes: spec.scopes } : {}),
        // Type-specific extras (oidc_issuer, team_id, key_id, private_key, …).
        ...(spec.details ?? {}),
      };
      // Only send the secret when given. On UPDATE, omitting it keeps the
      // stored one (so editing needs no secret re-entry).
      if (spec.clientSecret) details.client_secret = spec.clientSecret;
      const mapping = spec.attributeMapping ?? {
        email: "email",
        email_verified: "email_verified",
        username: "sub",
      };
      let exists = true;
      try {
        await cog.send(
          new DescribeIdentityProviderCommand({ UserPoolId: cfg.userPoolId, ProviderName: spec.name }),
        );
      } catch (e) {
        if ((e as { name?: string }).name !== "ResourceNotFoundException") throw e;
        exists = false;
      }
      if (exists) {
        await cog.send(
          new UpdateIdentityProviderCommand({
            UserPoolId: cfg.userPoolId,
            ProviderName: spec.name,
            ProviderDetails: details,
            AttributeMapping: mapping,
          }),
        );
      } else {
        if (!spec.clientSecret && !(spec.details && Object.keys(spec.details).length)) {
          throw new Error("credentials are required to set up a new provider");
        }
        await cog.send(
          new CreateIdentityProviderCommand({
            UserPoolId: cfg.userPoolId,
            ProviderName: spec.name,
            ProviderType: spec.type,
            ProviderDetails: details,
            AttributeMapping: mapping,
          }),
        );
      }
      const c = await spaClient();
      const providers = [...new Set([...(c.SupportedIdentityProviders ?? ["COGNITO"]), spec.name])];
      // A just-created provider isn't always visible to UpdateUserPoolClient
      // immediately — Cognito 400s with "identity provider ... does not exist".
      // Retry briefly instead of surfacing a scary error for a race that self-
      // heals in a second or two.
      let lastErr: unknown;
      for (let attempt = 0; attempt < 5; attempt++) {
        try {
          await patchClient({ providers });
          lastErr = undefined;
          break;
        } catch (e) {
          lastErr = e;
          const msg = e instanceof Error ? e.message : String(e);
          if (!/does not exist|InvalidParameter/i.test(msg)) throw e;
          await new Promise((r) => setTimeout(r, 800));
        }
      }
      if (lastErr) throw lastErr;
      await reconcile();
    },

    async removeProvider(name) {
      const c = await spaClient();
      await patchClient({
        providers: (c.SupportedIdentityProviders ?? ["COGNITO"]).filter((x) => x !== name),
      });
      await cog.send(
        new DeleteIdentityProviderCommand({ UserPoolId: cfg.userPoolId, ProviderName: name }),
      );
      await reconcile();
    },

    getPolicy: readPolicy,

    async setPolicy(policy) {
      const normalized = normalizeSignupPolicy(policy);
      if (normalized.allowedDomains.length) {
        // An email-domain allowlist can never match on a handle pool — storing
        // one would read as a rule that silently doesn't apply.
        const p = await pool();
        if (!(p.UsernameAttributes ?? []).includes("email")) {
          throw new Error(
            "allowedDomains can't apply to this pool — its accounts have no email addresses (handle mode)",
          );
        }
      }
      await s3.send(
        new PutObjectCommand({
          Bucket: cfg.bucket,
          Key: paths().authPolicy(),
          Body: JSON.stringify(normalizeSignupPolicy(policy), null, 2),
          ContentType: "application/json",
          CacheControl: "no-cache",
        }),
      );
      // Republish public methods so signup UIs see the new open/invite state.
      await reconcile();
    },
  };
}
